import UIComponent from "../ui/component.js";

const LOCALE_MAP = {
    ua: 'uk',
    no: 'nb',
};

export class MeetingWeekCalendar extends UIComponent {
    init() {
        this.rangeEl = this.element.querySelector('[data-tbx-name="weekRange"]');
        this.gridEl = this.element.querySelector('[data-tbx-name="weekGrid"]');
        this.emptyEl = this.element.querySelector('[data-tbx-name="weekEmpty"]');
        this.locale = this.resolveLocale();
        this.recordings = this.readRecordings();
        this.weekStart = this.startOfWeek(new Date());

        this.element.querySelector('[data-tbx-name="weekPrev"]')?.addEventListener('click', () => this.shiftWeek(-1));
        this.element.querySelector('[data-tbx-name="weekNext"]')?.addEventListener('click', () => this.shiftWeek(1));
        document.addEventListener('meeting-scheduled', (event) => this.addRecording(event.detail));

        this.render();
    }

    resolveLocale() {
        const raw = document.documentElement.lang || 'ru';
        return LOCALE_MAP[raw] || raw;
    }

    readRecordings() {
        const payload = this.element.querySelector('[data-tbx-name="recordingsPayload"]');
        if (!payload?.textContent) {
            return [];
        }

        try {
            const recordings = JSON.parse(payload.textContent);
            return Array.isArray(recordings) ? recordings : [];
        } catch (error) {
            return [];
        }
    }

    addRecording(recording) {
        if (!recording?.id) {
            return;
        }

        this.recordings = this.recordings.filter((item) => item.id !== recording.id);
        this.recordings.push(recording);
        this.weekStart = this.startOfWeek(new Date(recording.scheduled_at));
        this.render();
    }

    shiftWeek(offset) {
        const next = new Date(this.weekStart);
        next.setDate(next.getDate() + (offset * 7));
        this.weekStart = this.startOfWeek(next);
        this.render();
    }

    render() {
        if (!this.gridEl || !this.rangeEl) {
            return;
        }

        this.rangeEl.textContent = this.formatRange(this.weekStart);

        const days = Array.from({ length: 7 }, (_, index) => {
            const date = new Date(this.weekStart);
            date.setDate(this.weekStart.getDate() + index);
            return date;
        });

        this.gridEl.innerHTML = days.map((date) => this.renderDay(date)).join('');

        const hasEvents = days.some((date) => this.eventsFor(date).length > 0);
        this.emptyEl?.classList.toggle('d-none', hasEvents);
    }

    renderDay(date) {
        const events = this.eventsFor(date);
        const isToday = this.startOfDay(date).getTime() === this.startOfDay(new Date()).getTime();
        const weekday = new Intl.DateTimeFormat(this.locale, { weekday: 'short' }).format(date);
        const dayNumber = new Intl.DateTimeFormat(this.locale, { day: 'numeric' }).format(date);
        const eventsHtml = events.map((event) => this.renderEvent(event)).join('');

        return `
            <div class="meeting-week__day${isToday ? ' is-today' : ''}${events.length ? ' has-events' : ''}">
                <div class="meeting-week__day-head">
                    <span class="meeting-week__weekday">${this.escapeHtml(weekday)}</span>
                    <span class="meeting-week__date">${this.escapeHtml(dayNumber)}</span>
                </div>
                <div class="meeting-week__events">${eventsHtml}</div>
            </div>
        `;
    }

    renderEvent(event) {
        const date = new Date(event.scheduled_at);
        const time = new Intl.DateTimeFormat(this.locale, {
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
        const title = event.source_title || event.source || '';
        const inner = `
            <span class="meeting-week__event-time">${this.escapeHtml(time)}</span>
            <span class="meeting-week__event-title">${this.escapeHtml(title)}</span>
        `;

        if (!event.conference_url) {
            return `<div class="meeting-week__event">${inner}</div>`;
        }

        return `<a class="meeting-week__event" href="${this.escapeHtml(event.conference_url)}" target="_blank" rel="noopener noreferrer">${inner}</a>`;
    }

    eventsFor(date) {
        const start = this.startOfDay(date).getTime();
        const end = start + 24 * 60 * 60 * 1000;

        return this.recordings
            .filter((recording) => {
                const time = new Date(recording.scheduled_at).getTime();
                return Number.isFinite(time) && time >= start && time < end;
            })
            .sort((left, right) => new Date(left.scheduled_at) - new Date(right.scheduled_at));
    }

    formatRange(start) {
        const end = new Date(start);
        end.setDate(end.getDate() + 6);

        const startDay = new Intl.DateTimeFormat(this.locale, { day: 'numeric' }).format(start);
        const endDay = new Intl.DateTimeFormat(this.locale, { day: 'numeric' }).format(end);
        const endMonth = new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric' }).format(end);

        if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
            return `${startDay}–${endDay} ${endMonth}`;
        }

        const startMonth = new Intl.DateTimeFormat(this.locale, { month: 'long' }).format(start);
        return `${startDay} ${startMonth} – ${endDay} ${endMonth}`;
    }

    startOfWeek(date) {
        const start = this.startOfDay(date);
        const mondayOffset = (start.getDay() + 6) % 7;
        start.setDate(start.getDate() - mondayOffset);
        return start;
    }

    startOfDay(date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    escapeHtml(value) {
        return String(value)
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;');
    }
}
